import { useEffect, useMemo, useRef, useState } from "react";
import { propertyMediaApi } from "../../../api/propertyMediaApi";
import {
  buildFileFromClipboard,
  uploadPropertyMedia,
} from "../../../utils/propertyMediaUpload";
import { getSortedMediaByType } from "./propertyMediaHelpers";

export function usePropertyMediaManager({
  property,
  propertyId,
  onSave,
  mediaType,
  listMediaType,
  labelBase,
  defaultClipboardBase,
  userId,
}) {
  const uploadingRef = useRef(false);
  const pasteBoxRef = useRef(null);
  const queueRef = useRef([]);

  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [queue, setQueue] = useState([]);
  const [deletingId, setDeletingId] = useState(null);

  const savedMedia = useMemo(() => {
    const filterType = listMediaType === undefined ? mediaType : listMediaType;
    return getSortedMediaByType(property, filterType);
  }, [property, mediaType, listMediaType]);

  useEffect(() => {
    queueRef.current = queue;
  }, [queue]);

  useEffect(() => {
    return () => {
      queueRef.current.forEach((item) => {
        try {
          URL.revokeObjectURL(item.previewUrl);
        } catch {}
      });
    };
  }, []);

  useEffect(() => {
    function onPaste(event) {
      if (!pasteBoxRef.current) return;

      const target = event.target;
      const isWithinPasteBox = pasteBoxRef.current.contains(target);
      if (!isWithinPasteBox) return;

      const items = event.clipboardData?.items || [];
      const images = [];

      for (const item of items) {
        if (!item.type?.startsWith("image/")) continue;
        const file = item.getAsFile();
        if (file) images.push(file);
      }

      if (!images.length) return;

      event.preventDefault();

      const stamped = images.map((file, index) =>
        buildFileFromClipboard(file, labelBase, index, defaultClipboardBase),
      );

      addFilesToQueue(stamped, true);
    }

    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [defaultClipboardBase, labelBase]);

  function addFilesToQueue(files, fromClipboard = false) {
    const base = (labelBase || defaultClipboardBase).trim();
    const next = files.map((file, index) => {
      const id = `${Date.now()}-${Math.random().toString(16).slice(2)}-${index}`;
      const previewUrl = URL.createObjectURL(file);
      const cleanBase = base.replace(/\s+/g, " ").trim();
      const label = fromClipboard
        ? `${cleanBase} (paste ${index + 1})`
        : `${cleanBase} (${index + 1})`;

      return { id, file, previewUrl, label };
    });

    setQueue((prev) => [...next, ...prev]);
  }

  function onFileInputChange(event) {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length) return;
    addFilesToQueue(files, false);
  }

  function removeFromQueue(id) {
    setQueue((prev) => {
      const item = prev.find((entry) => entry.id === id);
      if (item?.previewUrl) {
        try {
          URL.revokeObjectURL(item.previewUrl);
        } catch {}
      }

      return prev.filter((entry) => entry.id !== id);
    });
  }

  function updateQueueLabel(id, label) {
    setQueue((prev) =>
      prev.map((entry) => (entry.id === id ? { ...entry, label } : entry)),
    );
  }

  async function uploadQueue() {
    if (!propertyId) return;
    if (uploadingRef.current) return;
    uploadingRef.current = true;

    setUploadError(null);

    if (!userId) {
      setUploadError("Não foi possível identificar o usuário logado...");
      uploadingRef.current = false;
      return;
    }

    if (!queue.length) {
      uploadingRef.current = false;
      return;
    }

    setUploading(true);
    try {
      for (const item of queue) {
        const response = await uploadPropertyMedia({
          file: item.file,
          auction_property_id: propertyId,
          media_type: mediaType,
          label: item.label,
          uploaded_by_user_id: userId,
        });

        if (!response?.success) {
          throw new Error(response?.error || "Falha no upload");
        }
      }

      queue.forEach((item) => {
        try {
          URL.revokeObjectURL(item.previewUrl);
        } catch {}
      });
      setQueue([]);

      onSave?.();
    } catch (error) {
      setUploadError(error?.message || "Erro no upload");
    } finally {
      setUploading(false);
      uploadingRef.current = false;
    }
  }

  async function onDeleteMedia(mediaId) {
    setDeletingId(mediaId);
    try {
      const response = await propertyMediaApi.remove(mediaId);
      if (!response?.success) {
        throw new Error(response?.error || "Falha ao deletar");
      }

      onSave?.();
    } catch (error) {
      setUploadError(error?.message || "Erro ao deletar mídia");
    } finally {
      setDeletingId(null);
    }
  }

  return {
    deletingId,
    onDeleteMedia,
    onFileInputChange,
    pasteBoxRef,
    queue,
    removeFromQueue,
    savedMedia,
    setUploadError,
    updateQueueLabel,
    uploadError,
    uploading,
    uploadQueue,
  };
}
